import { useState } from 'react';
import {
  Phone,
  UserPlus,
  StickyNote,
  ChevronDown,
  ChevronUp,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/Dialog';
import { toast } from '../ui/Toast';
import { api, type Lead, type LeadStatus } from '../../services/api';

const STATUS_CONFIG: Record<LeadStatus, { label: string; color: string; icon: any }> = {
  NEW: { label: 'Nuovo', color: 'bg-blue-100 text-blue-700', icon: Clock },
  CONTACTED: { label: 'Contattato', color: 'bg-yellow-100 text-yellow-700', icon: Phone },
  INTERESTED: { label: 'Interessato', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  NOT_INTERESTED: { label: 'Non interessato', color: 'bg-gray-100 text-gray-600', icon: XCircle },
  CONVERTED: { label: 'Convertito', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
};

interface LeadCardProps {
  lead: Lead;
  onUpdate: () => void;
}

export function LeadCard({ lead, onUpdate }: LeadCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isCallAttempt, setIsCallAttempt] = useState(true);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [imgError, setImgError] = useState(false);

  const StatusIcon = STATUS_CONFIG[lead.status].icon;
  const hasImage = lead.listingImage && !imgError;

  const handleCall = () => {
    if (lead.phone) {
      window.location.href = `tel:${lead.phone}`;
    }
  };

  const handleAddToContacts = () => {
    const vCard = `BEGIN:VCARD
VERSION:3.0
FN:${lead.name}
${lead.phone ? `TEL;TYPE=CELL:${lead.phone}` : ''}
${lead.email ? `EMAIL:${lead.email}` : ''}
NOTE:Lead HomeFinder${lead.listingTitle ? ` - ${lead.listingTitle}` : ''}
END:VCARD`;

    const blob = new Blob([vCard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${lead.name.replace(/\s+/g, '_')}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({ title: 'Contatto scaricato', variant: 'success' });
  };

  const handleStatusChange = async (newStatus: LeadStatus) => {
    try {
      await api.updateLead(lead.id, { status: newStatus });
      onUpdate();
    } catch {
      toast({ title: 'Errore aggiornamento stato', variant: 'error' });
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setIsSavingNote(true);
    try {
      await api.addLeadNote(lead.id, {
        text: newNote.trim(),
        isCallAttempt,
      });
      setNewNote('');
      setIsCallAttempt(true);
      setIsNoteDialogOpen(false);
      onUpdate();
      toast({
        title: isCallAttempt ? 'Nota e tentativo registrato' : 'Nota aggiunta',
        variant: 'success',
      });
    } catch {
      toast({ title: 'Errore nel salvataggio', variant: 'error' });
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteLead(lead.id);
      onUpdate();
      toast({ title: 'Lead eliminato', variant: 'success' });
    } catch {
      toast({ title: 'Errore eliminazione', variant: 'error' });
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Main row */}
        <div className="p-3">
          <div className="flex items-start gap-3">
            {/* Listing image as avatar */}
            <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden bg-gray-100">
              {hasImage ? (
                <img
                  src={lead.listingImage!}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 text-white font-bold text-lg">
                  {lead.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              {/* Name + status */}
              <h3 className="font-semibold text-gray-900 text-[15px] leading-tight mb-0.5">
                {lead.name}
              </h3>

              {/* Status badge + price inline */}
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${STATUS_CONFIG[lead.status].color}`}>
                  <StatusIcon className="h-2.5 w-2.5" />
                  {STATUS_CONFIG[lead.status].label}
                </span>
                {lead.listingPrice != null && (
                  <span className="text-xs font-semibold text-gray-700">
                    {Number(lead.listingPrice).toLocaleString('it-IT')} EUR
                  </span>
                )}
              </div>

              {/* Listing title */}
              {lead.listingTitle && (
                <p className="text-xs text-gray-400 truncate">{lead.listingTitle}</p>
              )}

              {/* Phone + call attempts */}
              <div className="flex items-center gap-3 mt-1">
                {lead.phone && (
                  <span className="text-xs text-gray-500">{lead.phone}</span>
                )}
                {lead.callAttempts > 0 && (
                  <span className="text-xs text-orange-600 font-medium inline-flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {lead.callAttempts}x
                  </span>
                )}
              </div>
            </div>

            {/* Expand toggle */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors mt-1"
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>

          {/* Quick actions row - always visible */}
          <div className="flex items-center gap-2 mt-2.5 ml-[68px]">
            {lead.phone && (
              <button
                onClick={handleCall}
                className="h-8 px-3 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 text-xs font-medium inline-flex items-center gap-1.5 transition-colors"
              >
                <Phone className="h-3.5 w-3.5" />
                Chiama
              </button>
            )}
            <button
              onClick={() => setIsNoteDialogOpen(true)}
              className="h-8 px-3 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-medium inline-flex items-center gap-1.5 transition-colors"
            >
              <StickyNote className="h-3.5 w-3.5" />
              Nota
            </button>
            <button
              onClick={handleAddToContacts}
              className="h-8 px-3 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium inline-flex items-center gap-1.5 transition-colors"
            >
              <UserPlus className="h-3.5 w-3.5" />
              Rubrica
            </button>
          </div>
        </div>

        {/* Expanded */}
        {isExpanded && (
          <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-4">
            {/* Source link */}
            {lead.sourceUrl && (
              <a
                href={lead.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
              >
                <ExternalLink className="h-3 w-3" />
                Vedi annuncio originale
              </a>
            )}

            {/* Contact details */}
            {(lead.email || lead.propertyAddress) && (
              <div className="space-y-1.5">
                {lead.email && (
                  <p className="text-xs text-gray-600">
                    Email: <a href={`mailto:${lead.email}`} className="text-blue-600">{lead.email}</a>
                  </p>
                )}
                {lead.propertyAddress && (
                  <p className="text-xs text-gray-600">Indirizzo: {lead.propertyAddress}</p>
                )}
              </div>
            )}

            {/* Status change */}
            <div>
              <p className="text-xs text-gray-500 mb-2">Stato</p>
              <div className="flex flex-wrap gap-1.5">
                {(Object.entries(STATUS_CONFIG) as [LeadStatus, typeof STATUS_CONFIG[LeadStatus]][]).map(
                  ([status, config]) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      className={`text-[10px] px-2.5 py-1 rounded-full transition-all ${
                        lead.status === status
                          ? config.color + ' ring-2 ring-offset-1 ring-current'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {config.label}
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500">Note ({lead.notes?.length || 0})</p>
                <button
                  onClick={() => setIsNoteDialogOpen(true)}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  + Aggiungi
                </button>
              </div>
              {lead.notes && lead.notes.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {lead.notes.map((note) => (
                    <div
                      key={note.id}
                      className={`p-2.5 rounded-lg text-sm ${
                        note.isCallAttempt
                          ? 'bg-orange-50 border border-orange-100'
                          : 'bg-white border border-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {note.isCallAttempt && <Phone className="h-3 w-3 text-orange-500" />}
                        <span className="text-[10px] text-gray-400">{formatDate(note.createdAt)}</span>
                      </div>
                      <p className="text-gray-700 text-xs">{note.text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">Nessuna nota</p>
              )}
            </div>

            {/* Delete */}
            <button
              onClick={handleDelete}
              className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1"
            >
              <Trash2 className="h-3 w-3" />
              Elimina lead
            </button>
          </div>
        )}
      </div>

      {/* Add Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Aggiungi nota - {lead.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Nota</label>
              <textarea
                placeholder="Scrivi una nota..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="w-full h-28 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
              />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isCallAttempt}
                onChange={(e) => setIsCallAttempt(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                <Phone className="inline h-3.5 w-3.5 mr-1 text-orange-500" />
                Registra come tentativo di chiamata
              </span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsNoteDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleAddNote} disabled={!newNote.trim() || isSavingNote}>
              {isSavingNote ? 'Salvataggio...' : 'Salva nota'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
